// src/pages/resume/projects.js
import React, { useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AddItemButton from '../../components/common/AddItemButton';
import FormInput from '../../components/resume/FormInput';
import FormTextarea from '../../components/resume/FormTextarea';
import { useResume } from '../../context/ResumeContext';
import useAutosave from '../../hooks/useAutosave';
import SaveStatusChip from '../../components/resume/editor/SaveStatusChip';
import FeedbackModal from '../../components/common/FeedbackModal';

// --- ProjectItem Component ---
// No save logic, just displays data and calls update functions
const ProjectItem = ({ project, index, onUpdate, onDelete, onAiWrite }) => {
    const remainingAiWrites = 3 - (project.aiClickCount || 0);

    return (
        <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold">
                    {project.name || `Project ${index + 1}`}
                </h2>
                <button onClick={() => onDelete(project.id)} className="bg-white/10 hover:bg-red-500/20 hover:text-red-300 text-gray-400 flex-shrink-0 ml-4 rounded p-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
            </div>
            {/* REMOVED: onSubmit from form tag */}
            <form className="space-y-6">
                <FormInput label="PROJECT NAME *" name="name" value={project.name} onChange={(e) => onUpdate(project.id, { ...project, name: e.target.value })} placeholder="e.g., AI Resume Optimizer" />
                <FormInput label="DATE" name="date" value={project.date} onChange={(e) => onUpdate(project.id, { ...project, date: e.target.value })} placeholder="e.g., May 2024" />

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-bold text-gray-400 uppercase">PROJECT DESCRIPTION & RELEVANCE</label>
                        <div className="flex items-center space-x-2">
                            <button
                                type="button"
                                onClick={() => onAiWrite(project.id)}
                                disabled={remainingAiWrites <= 0}
                                className="bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold py-1 px-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                AI Write
                            </button>
                            <span className="text-xs text-gray-400">({remainingAiWrites} left)</span>
                        </div>
                    </div>
                    <FormTextarea
                        name="relevance"
                        value={project.relevance}
                        onChange={(e) => onUpdate(project.id, { ...project, relevance: e.target.value })}
                        rows="6"
                    />
                </div>

                {/* --- REMOVED: Individual Save Button --- */}
            </form>
        </div>
    );
};

// --- Projects Component (Main) ---
const Projects = () => {
    const { projects, setProjects, addProject } = useResume();
    const { resumeId } = useParams();
    const [modalInfo, setModalInfo] = useState({ isOpen: false, title: '', message: '', isError: false });
    const idMapRef = useRef(new Map());

    const updateProject = useCallback((id, updatedData) => {
        setProjects(currentProjects =>
            currentProjects.map(proj => (proj.id === id ? updatedData : proj))
        );
    }, [setProjects]);

    const deleteProject = (id) => {
        if (projects.length > 1) {
            setProjects(projects.filter(proj => proj.id !== id));
        } else {
            setModalInfo({ isOpen: true, title: 'Notice', message: 'You must have at least one project entry.', isError: false });
        }
    };

    // --- ADDED: saveAllProjects function ---
    const saveAllProjects = async () => {
        if (!resumeId) throw new Error('no resume ID');

        let saveErrors = 0;
        const savePromises = projects.map(proj => {
            // Only save if it has a name
            if (proj.name) {
                const effectiveId = idMapRef.current.get(proj.id) ?? proj.id;
                return fetch('https://renaisons.com/api/save_project.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...proj, id: effectiveId, resume_id: resumeId }),
                })
                    .then(response => response.json())
                    .then(result => {
                        if (result.status === 'success' && result.project_id) {
                            idMapRef.current.set(proj.id, result.project_id);
                            return { oldId: proj.id, newId: result.project_id };
                        } else if (result.status !== 'success') {
                            saveErrors++;
                            console.error(`Error saving project "${proj.name || 'New'}": ${result.message}`);
                        }
                        return null;
                    })
                    .catch(error => {
                        saveErrors++;
                        console.error(`Network error saving project "${proj.name || 'New'}":`, error);
                        return null;
                    });
            }
            return Promise.resolve(null);
        });

        try {
            const results = await Promise.all(savePromises);

            const updates = results.filter(Boolean);
            if (updates.length > 0) {
                setProjects(currentProjects =>
                    currentProjects.map(proj => {
                        const update = updates.find(u => u.oldId === proj.id);
                        return update ? { ...proj, id: update.newId } : proj;
                    })
                );
            }

            if (saveErrors > 0) {
                throw new Error('some entries failed');
            }

        } catch (error) {
            console.error('Error during batch save:', error);
            throw error;
        }
    };

    const validateProjects = () => {
        const hasAnyData = projects.some(proj => proj.name || proj.date || proj.relevance);
        if (!hasAnyData) return null;
        const hasInvalid = projects.some(proj => (proj.date || proj.relevance) && !proj.name);
        return hasInvalid ? false : true;
    };

    const { status, retry } = useAutosave({ sectionKey: 'projects', data: projects, save: saveAllProjects, validate: validateProjects });

    const handleAiWrite = (id) => {
        const projectToUpdate = projects.find(proj => proj.id === id);
        const currentClickCount = projectToUpdate.aiClickCount || 0;

        if (currentClickCount >= 3) {
            setModalInfo({ isOpen: true, title: 'Notice', message: 'AI suggestion limit reached for this item.', isError: false });
            return;
        }

        const aiSuggestions = [
            'â€¢ Developed a full-stack web application using React, Node.js, and PostgreSQL to track user analytics.',
            'â€¢ Deployed a machine learning model on AWS SageMaker with a CI/CD pipeline, reducing inference time by 30%.',
            'â€¢ Led a team of 3 to build a mobile app, resulting in 10,000+ downloads in the first month.'
        ];

        const aiGeneratedText = aiSuggestions[currentClickCount];
        const currentRelevance = projectToUpdate.relevance.trim();
        const newRelevance = (currentRelevance === 'â€¢' || currentRelevance === '')
            ? aiGeneratedText
            : `${currentRelevance}\n${aiGeneratedText}`;

        updateProject(id, { ...projectToUpdate, relevance: newRelevance, aiClickCount: currentClickCount + 1 });
    };

    return (
        <>
            {modalInfo.isOpen && (
                <FeedbackModal
                    title={modalInfo.title}
                    message={modalInfo.message}
                    isError={modalInfo.isError}
                    onClose={() => setModalInfo({ ...modalInfo, isOpen: false })}
                />
            )}
            {projects.map((proj, index) => (
                <ProjectItem
                    key={proj.id}
                    project={proj}
                    index={index}
                    onUpdate={updateProject}
                    onDelete={deleteProject}
                    onAiWrite={handleAiWrite}
                />
            ))}
            <div className="flex items-center justify-between mt-4">
                <AddItemButton onClick={addProject}>ADD ANOTHER PROJECT</AddItemButton>
                <SaveStatusChip status={status} onRetry={retry} />
            </div>
        </>
    );
};

export default Projects;
